/**
 * Synthetic fixture for the multi-entity roll-up CONCEPT screen.
 *
 * Roadmap FR-2 (`docs/demo/tradeshow-2026-09.md`, "Static booth material" —
 * *"You run five LLCs. Nobody shows you what they add up to. We will."*).
 * `BusinessEntity` in `src/api/types.ts` already carries the comment "the demo
 * seeds one per tenant; real customers run 2-6 (roadmap FR-2)" — this is that
 * roadmap item, as a static picture rather than a wired feature.
 *
 * NOT wired to any API. The real demo deliberately seeds exactly one entity
 * per tenant (`db/seed/0002_seed_demo.sql`) — a second entity would add a
 * switcher a visitor has to understand in the ten seconds they'll give the
 * quick-entry flow. This screen exists to show Christy's prospects what the
 * product looks like once a customer's real entity count rolls up to one
 * owner total: a view that today only exists once a year, when an accountant
 * builds it by hand.
 *
 * Extends the fictional world already seeded for the booth rather than
 * inventing a separate cast: state "MN" and the entity name "Larson Family
 * Farms" come straight from `db/seed/0002_seed_demo.sql`; the owner name "Sam
 * Larson" comes from the original design mock
 * (`easyfarm-engineering/demo/easyfarm-cloud-demo.html`, the signed-in user
 * avatar "SL" / "Sam Larson"). Never real customer entities or figures —
 * demo spec "Never a real customer .edb".
 */

export interface RollupEntity {
  id: string;
  name: string;
  state: string;
  /** How the entity is taxed — determines which K-1 box its income lands in. */
  structure: string;
  description: string;
  k1Box: string;
  /** Whole-dollar synthetic amount allocated to the owner via K-1. */
  allocatedAmount: number;
}

export const ROLLUP_OWNER_NAME = "Sam Larson";

export const ROLLUP_ENTITIES: RollupEntity[] = [
  {
    id: "rollup-operating",
    name: "Larson Family Farms, LLC",
    state: "MN",
    structure: "LLC — taxed as a partnership",
    description: "Grain and soybean operation",
    k1Box: "Box 1 — Ordinary business income",
    allocatedAmount: 142_300,
  },
  {
    id: "rollup-land",
    name: "Larson Land Holdings, LLC",
    state: "MN",
    structure: "LLC — taxed as a partnership",
    description: "Cropland and pasture, leased to the operating entity",
    k1Box: "Box 2 — Net rental real estate income",
    allocatedAmount: 58_900,
  },
  {
    id: "rollup-cattle",
    name: "Larson Cattle Co., LLC",
    state: "MN",
    structure: "LLC — taxed as a partnership",
    description: "Cow-calf herd",
    k1Box: "Box 1 — Ordinary business income",
    allocatedAmount: 36_150,
  },
];

/**
 * Derived, not stored — per the state-management rule "compute it, do not
 * store it." This is a plain sum of three fixed whole-dollar synthetic
 * figures with no floating-point precision concern, unlike the live Entry
 * feed the rest of the app deliberately never sums client-side (see
 * `web/README.md` "Known limitations" #1) — there is no wire data here to
 * distrust, just three constants defined two lines above.
 */
export function rollupTotal(entities: RollupEntity[] = ROLLUP_ENTITIES): number {
  return entities.reduce((sum, entity) => sum + entity.allocatedAmount, 0);
}
